import { notify } from '@/lib/toast';
import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { CreditCard, Plus, Ban, Banknote, Building, FileText, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { Dialog } from 'primereact/dialog';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui/pagination-bar';
import api from '@/lib/api';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { isBankSettledMethod } from '@/lib/bank-accounts';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import toast from 'react-hot-toast';
import { Label } from '@/components/ui/label';
import { RecordLink } from '@/components/records/RecordLink';
import { useRowOpen } from '@/components/records/useRowOpen';

const METHOD_STYLES: Record<string, string> = {
  CASH: 'bg-warning/10 text-warning-text ring-warning',
  BANK_TRANSFER: 'bg-primary/10 text-primary-text ring-primary',
  CHEQUE: 'bg-primary/10 text-primary-text ring-primary',
};

const STATUS_STYLES: Record<string, string> = {
  POSTED: 'bg-success/10 text-success-text ring-success',
  DRAFT: 'bg-warning/10 text-warning-text ring-warning',
  PENDING_APPROVAL: 'bg-info/10 text-info-text ring-info',
  REJECTED: 'bg-destructive/10 text-destructive-text ring-destructive',
  VOIDED: 'bg-destructive/10 text-destructive-text ring-destructive',
};

type MoneyValue = number | string;

interface SupplierOption {
  id: string;
  name: string;
}

interface BankAccountOption {
  id: string;
  name: string;
  currentBalance?: MoneyValue | null;
  openingBalance?: MoneyValue | null;
}

interface SupplierBalanceOrder {
  id: string;
  orderNumber: string;
  totalAmount: MoneyValue;
  paidAmount: MoneyValue;
}

interface SupplierBalance {
  totalOwed: MoneyValue;
  totalPaid: MoneyValue;
  orders?: SupplierBalanceOrder[];
}

interface VendorPayment {
  id: string;
  purchaseOrderId?: string | null;
  paymentNumber: string;
  supplier?: { name?: string } | null;
  purchaseOrder?: { id?: string; orderNumber?: string } | null;
  amount: MoneyValue;
  paymentMethod: string;
  paymentDate: string;
  status: string;
}

interface VendorPaymentsResponse {
  data?: VendorPayment[];
  meta?: { total?: number };
}

interface CreateVendorPaymentPayload {
  supplierId: string;
  purchaseOrderId?: string;
  bankAccountId?: string;
  amount: number;
  paymentMethod: string;
  reference: string;
  paymentDate: string;
  notes: string;
}

const emptyForm = {
  supplierId: '',
  purchaseOrderId: '',
  bankAccountId: '',
  amount: '',
  paymentMethod: 'CASH',
  reference: '',
  paymentDate: new Date().toISOString().split('T')[0],
  notes: '',
};

export function VendorPaymentsPage() {
  const rowOpen = useRowOpen();
  const { t } = useTranslation('finance');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [voidDialogId, setVoidDialogId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [form, setForm] = useState({ ...emptyForm });
  const [selectedSupplierBalance, setSelectedSupplierBalance] = useState<SupplierBalance | null>(null);
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('purchases:write') || hasPermission('purchases:*');
  const canVoid = hasPermission('purchases:write') || hasPermission('purchases:*');
  const canApprove = hasPermission('vendor-payments:approve') || hasPermission('bank:*');
  const [rejectDialogId, setRejectDialogId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handlePaymentUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['vendor-payments'] });
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  }, [queryClient]);
  useSocketEvent('purchase.received', handlePaymentUpdate);

  const { data, isLoading } = useQuery<VendorPaymentsResponse>({
    queryKey: ['vendor-payments', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const res = await api.get(`/vendor-payments?${params}`);
      return res.data;
    },
  });

  const { data: suppliers = [] } = useQuery<SupplierOption[]>({
    queryKey: ['suppliers-active'],
    queryFn: async () => {
      const res = await api.get('/suppliers');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const { data: bankAccounts = [] } = useQuery<BankAccountOption[]>({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const res = await api.get('/bank-accounts');
      return res.data ?? [];
    },
  });

  const { data: pendingCount } = useQuery<number>({
    queryKey: ['vendor-payments-pending-count'],
    queryFn: async () => {
      const res = await api.get('/vendor-payments/pending-count');
      return res.data?.count ?? res.data ?? 0;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: CreateVendorPaymentPayload) => api.post('/vendor-payments', data),
    onSuccess: () => {
      toast.success(t('paymentRecordedAndPosted'));
      queryClient.invalidateQueries({ queryKey: ['vendor-payments'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payments-pending-count'] });
      closeDialog();
    },
    onError: (err) => notify.error(err, t('failedToRecordPayment')),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/vendor-payments/${id}/approve`),
    onSuccess: () => {
      toast.success(t('paymentApproved'));
      queryClient.invalidateQueries({ queryKey: ['vendor-payments'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payments-pending-count'] });
    },
    onError: (err) => notify.error(err, t('failedToApprovePayment')),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.patch(`/vendor-payments/${id}/reject`, { reason }),
    onSuccess: () => {
      toast.success(t('paymentRejected'));
      queryClient.invalidateQueries({ queryKey: ['vendor-payments'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-payments-pending-count'] });
      setRejectDialogId(null);
      setRejectReason('');
    },
    onError: (err) => notify.error(err, t('failedToRejectPayment')),
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.patch(`/vendor-payments/${id}/void`, { reason }),
    onSuccess: () => {
      toast.success(t('paymentVoided'));
      queryClient.invalidateQueries({ queryKey: ['vendor-payments'] });
      setVoidDialogId(null);
      setVoidReason('');
    },
    onError: (err) => notify.error(err, t('failedToVoidPayment')),
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setForm({ ...emptyForm });
    setSelectedSupplierBalance(null);
  };

  const handleSupplierChange = async (supplierId: string) => {
    setForm({ ...form, supplierId, purchaseOrderId: '' });
    if (supplierId) {
      try {
        const res = await api.get(`/vendor-payments/supplier/${supplierId}/balance`);
        setSelectedSupplierBalance(res.data);
      } catch {
        setSelectedSupplierBalance(null);
      }
    } else {
      setSelectedSupplierBalance(null);
    }
  };

  const handleSave = () => {
    if (!form.supplierId || !form.amount || !form.paymentDate || !form.paymentMethod) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error(t('amountMustBePositive'));
      return;
    }
    if (isBankSettledMethod(form.paymentMethod) && bankAccounts.length > 1 && !form.bankAccountId) {
      toast.error(t('selectBankAccountForMethod'));
      return;
    }
    saveMutation.mutate({
      ...form,
      amount: Number(form.amount),
      purchaseOrderId: form.purchaseOrderId || undefined,
      bankAccountId: form.bankAccountId || undefined,
    });
  };

  const payments = data?.data ?? [];
  const meta = data?.meta ?? {};
  const totalAmount = payments.reduce((s: number, p: VendorPayment) => s + Number(p.amount), 0);

  const selectedPO = useMemo(
    () => selectedSupplierBalance?.orders?.find(
      (o: SupplierBalanceOrder) => o.id === form.purchaseOrderId,
    ),
    [selectedSupplierBalance, form.purchaseOrderId],
  );
  const maxAmount = selectedPO
    ? Number(selectedPO.totalAmount) - Number(selectedPO.paidAmount)
    : undefined;

  return (
    <div className="space-y-6 page-enter">
      {/* Header */}
      <PageHeader
        title={t('vendorPayments')}
        subtitle={t('recordAndTrackPayments')}
        actions={canCreate && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="me-2 h-4 w-4" />
            {t('newPayment')}
          </Button>
        )}
      />

      {canApprove && (pendingCount ?? 0) > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-info/30 bg-info/10 px-4 py-3">
          <Clock className="h-5 w-5 text-info-text" />
          <p className="text-sm font-medium text-info-text">
            {t('paymentsAwaitingApproval', { count: pendingCount })}
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label={t('totalPayments')} value={meta.total ?? payments.length} format="number" icon={CreditCard} />
        <StatCard label={t('amountPaidPage')} value={totalAmount} format="currency" icon={Banknote} accent="success" />
        <StatCard label={t('suppliersLabel')} value={suppliers.length} format="number" icon={Building} />
      </div>

      {/* Table */}
      <Card className="animate-fade-in-up stagger-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('paymentRecords')}</CardTitle>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('searchPayments')}
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} cols={8} />
          ) : !payments.length ? (
            <EmptyState
              icon={CreditCard}
              title={t('noPaymentsRecorded')}
              subtitle={t('startRecordingPayments')}
              action={canCreate ? { label: t('newPayment'), onClick: () => setDialogOpen(true) } : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('paymentNo')}</Th>
                    <Th>{t('supplier')}</Th>
                    <Th>{t('poRef')}</Th>
                    <Th className="text-end">{t('amount')}</Th>
                    <Th>{t('method')}</Th>
                    <Th>{t('date')}</Th>
                    <Th>{t('status')}</Th>
                    <Th className="text-end">{t('actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {payments.map((p: VendorPayment) => (
                    <Tr key={p.id} {...rowOpen('vendorPayment', p.id, p.paymentNumber)}>
                      <Td label={t('paymentNo')} className="font-mono text-xs">
                        <RecordLink type="vendorPayment" id={p.id} label={p.paymentNumber} variant="mono" className="text-xs" />
                      </Td>
                      <Td label={t('supplier')} className="font-medium">{p.supplier?.name ?? '—'}</Td>
                      <Td label={t('poRef')} className="text-muted-foreground font-mono text-xs">
                        {p.purchaseOrder?.orderNumber ? (
                          <RecordLink type="purchaseOrder" id={p.purchaseOrderId ?? p.purchaseOrder.id} label={p.purchaseOrder.orderNumber} variant="mono" className="text-xs" />
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td label={t('amount')} className="text-end font-semibold">
                        {formatCurrency(Number(p.amount))}
                      </Td>
                      <Td label={t('method')}>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                            METHOD_STYLES[p.paymentMethod] ?? '',
                          )}
                        >
                          {p.paymentMethod.replace('_', ' ')}
                        </span>
                      </Td>
                      <Td label={t('date')} className="text-muted-foreground">
                        {formatDate(p.paymentDate)}
                      </Td>
                      <Td label={t('status')}>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                            STATUS_STYLES[p.status] ?? '',
                          )}
                        >
                          {p.status}
                        </span>
                      </Td>
                      <Td label={t('actions')} className="text-end" onClick={(e) => e.stopPropagation()}>
                        {p.status === 'PENDING_APPROVAL' && canApprove && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => approveMutation.mutate(p.id)}
                              disabled={approveMutation.isPending}
                              className="rounded-lg p-1.5 hover:bg-accent transition-colors disabled:opacity-50"
                              title={t('approve')}
                            >
                              <CheckCircle className="h-4 w-4 text-success-text" />
                            </button>
                            <button
                              onClick={() => setRejectDialogId(p.id)}
                              className="rounded-lg p-1.5 hover:bg-accent transition-colors"
                              title={t('reject')}
                            >
                              <XCircle className="h-4 w-4 text-destructive-text" />
                            </button>
                          </div>
                        )}
                        {p.status === 'POSTED' && canVoid && (
                          <button
                            onClick={() => setVoidDialogId(p.id)}
                            className="rounded-lg p-1.5 hover:bg-accent transition-colors"
                            title="Void payment"
                          >
                            <Ban className="h-4 w-4 text-destructive-text" />
                          </button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>

              {/* Pagination */}
              {(meta.total ?? 0) > 0 && (
                <div className="mt-4">
                  <PaginationBar
                    page={page}
                    pageSize={10}
                    total={meta.total ?? payments.length}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        visible={dialogOpen}
        onHide={closeDialog}
        header={t('recordVendorPayment')}
        style={{ width: '52rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={closeDialog}>{t('cancel')}</Button>
            <Button
              className="flex-1 bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground shadow-md shadow-primary/20"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? t('recording') : t('recordPayment')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t('supplier')} <span className="text-destructive-text">*</span>
            </Label>
            <Combobox
              options={suppliers.map((s: SupplierOption) => ({ value: s.id, label: s.name }))}
              value={form.supplierId}
              onChange={(val) => handleSupplierChange(val || '')}
              placeholder={t('selectSupplier')}
              searchPlaceholder={t('searchSuppliers')}
              emptyText={t('noSuppliersFound')}
            />
          </div>

          {/* Supplier balance info */}
          {selectedSupplierBalance && (
            <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('totalOwed')}</span>
                <span className="font-semibold text-destructive-text">{formatCurrency(Number(selectedSupplierBalance.totalOwed))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('totalPaid')}</span>
                <span className="font-semibold text-success-text">{formatCurrency(Number(selectedSupplierBalance.totalPaid))}</span>
              </div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="font-medium">{t('outstanding')}</span>
                <span className="font-bold">{formatCurrency(Number(selectedSupplierBalance.totalOwed) - Number(selectedSupplierBalance.totalPaid))}</span>
              </div>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">{t('purchaseOrderOptional')}</Label>
            <Combobox
              options={
                selectedSupplierBalance?.orders?.map((o: SupplierBalanceOrder) => ({
                  value: o.id,
                  label: `${o.orderNumber} — ${t('outstanding')}: ${formatCurrency(Number(o.totalAmount) - Number(o.paidAmount))}`,
                })) ?? []
              }
              value={form.purchaseOrderId}
              onChange={(val) => setForm({ ...form, purchaseOrderId: val || '' })}
              placeholder={t('linkToPo')}
              emptyText={t('noOutstandingPos')}
            />
            {maxAmount !== undefined && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('outstanding')}: <strong>{formatCurrency(maxAmount)}</strong>
              </p>
            )}
          </div>

          <div>
            <Label className="mb-1.5 block">
              {t('paymentMethod')} <span className="text-destructive-text">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'CASH', label: t('cash'), icon: Banknote },
                { value: 'BANK_TRANSFER', label: t('bankTransfer'), icon: Building },
                { value: 'CHEQUE', label: t('chequeMethodLabel'), icon: FileText },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm({ ...form, paymentMethod: value, bankAccountId: isBankSettledMethod(value) ? form.bankAccountId : '' })}
                  className={cn(
                    'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors',
                    form.paymentMethod === value
                      ? 'border-primary bg-primary/10 text-primary-text'
                      : 'hover:bg-accent',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isBankSettledMethod(form.paymentMethod) && (
            <div>
              <Label className="mb-1.5 block">
                {t('bankAccount')}{bankAccounts.length > 1 && <span className="text-destructive-text"> *</span>}
              </Label>
              <Combobox
                options={bankAccounts.map((b: BankAccountOption) => ({
                  value: b.id,
                  label: `${b.name} (${formatCurrency(Number(b.currentBalance ?? b.openingBalance))})`,
                }))}
                value={form.bankAccountId}
                onChange={(val) => setForm({ ...form, bankAccountId: val || '' })}
                placeholder={t('selectBankAccount')}
                emptyText={t('noBankAccountsYet')}
              />
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">
              {t('amount')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              max={maxAmount}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">
              {t('paymentDate')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              type="date"
              value={form.paymentDate}
              onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('reference')}</Label>
            <Input
              placeholder={t('referencePlaceholder')}
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Textarea
              placeholder={t('notesPlaceholder')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <Dialog
        visible={!!voidDialogId}
        onHide={() => { setVoidDialogId(null); setVoidReason(''); }}
        header={t('voidPayment')}
        style={{ width: '48rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => { setVoidDialogId(null); setVoidReason(''); }}>{t('cancel')}</Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => voidMutation.mutate({ id: voidDialogId!, reason: voidReason })}
              disabled={!voidReason.trim() || voidMutation.isPending}
            >
              {voidMutation.isPending ? t('voiding') : t('voidPayment')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground mb-4">
          {t('voidPaymentWarning')}
        </p>
        <div>
          <Label className="mb-1.5 block">
            {t('reason')} <span className="text-destructive-text">*</span>
          </Label>
          <Textarea
            placeholder={t('voidReasonPlaceholder')}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            rows={3}
          />
        </div>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog
        visible={!!rejectDialogId}
        onHide={() => { setRejectDialogId(null); setRejectReason(''); }}
        header={t('rejectPayment')}
        style={{ width: '48rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => { setRejectDialogId(null); setRejectReason(''); }}>{t('cancel')}</Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => rejectMutation.mutate({ id: rejectDialogId!, reason: rejectReason })}
              disabled={!rejectReason.trim() || rejectMutation.isPending}
            >
              {rejectMutation.isPending ? t('rejecting') : t('rejectPayment')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground mb-4">
          {t('rejectPaymentWarning')}
        </p>
        <div>
          <Label className="mb-1.5 block">
            {t('reason')} <span className="text-destructive-text">*</span>
          </Label>
          <Textarea
            placeholder={t('rejectReasonPlaceholder')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
        </div>
      </Dialog>
    </div>
  );
}
